// src/components/shared/CalculationExplanationPage.tsx
// דף הסברי חישוב - נגיש למשתמשים מחוברים בלבד

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEnduserAuth } from "../../contexts/EnduserAuthContext";
import AccessibilityWidget from "./AccessibilityWidget";

const CalculationExplanationPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useEnduserAuth();

  // הגנה - רק למשתמשים מחוברים
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/enduser/auth");
    }
  }, [isAuthenticated, navigate]);

  // אם לא מחובר - לא להציג כלום
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Accessibility Widget */}
      <AccessibilityWidget />

      <div
        className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
        dir="rtl"
      >
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8 sm:p-12">
          {/* Header with Back Button */}
          <div className="mb-8 flex items-center justify-between border-b pb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                📘 דף הסברי חישוב
              </h1>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              aria-label="חזור לעמוד הקודם"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="hidden sm:inline">חזור</span>
            </button>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* מבוא */}
            <section className="mb-12">
              <div className="bg-blue-50 border-r-4 border-blue-400 p-6 rounded-lg mb-8">
                <p className="text-lg text-gray-800 leading-relaxed">
                  <strong>שלום רב,</strong>
                  <br />
                  בדף זה תקבלו הרחבה והסברים על סעיפי המערכת השונים.
                </p>
              </div>
            </section>

            {/* 1. ערך קניה במט"ח */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                1. ערך קניה במט"ח
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  זהו המחיר שבו יירכש כלי הרכב. הערך צריך להיות במטח וצריך להיות
                  נטו כלומר לפני מעמ מקומי בחו"ל.
                </p>

                <p>
                  ישנם ספקים אשר יגבו מכם את ערך המעמ ויחזירו אותו בשלבים שונים
                  לאחר הרכישה.
                </p>

                <p>
                  ברגע שכלי רכב עולה לאוניה אם בתוך מכולה או אם על גבי האוניה,
                  מונפק שטר מטען שמבטא את ההובלה הצפויה לישראל.
                </p>

                <p>
                  בדרך כלל במדינות אירופה נוטים לגבות את המעמ ולהחזיר אותו
                  כשמונפק שטר מטען ולאחר תהליך ייצוא באותה המדינה.
                </p>

                <p>
                  הספקים מנפיקים חשבונית(credit note) זיכוי ומחזירים את המעמ
                  לחשבון הקונה.
                </p>

                <p>
                  בשונה מאירופה בארהב לא תוכלו לקנות ישירות מהיצרן שכן אין לו
                  אינטרס מסחרי למכור לכם.
                </p>

                <p>
                  אתם תצטרכו למצוא דילר מורשה שימכור לכם את הרכב ובדרך כלל ללא
                  מרכיב המעמ.
                </p>

                <p>
                  מיתר מדינות העולם העיקרון הוא זהה, איננו רוצים לשלם את המעמ
                  שכן במידה ולא קיבלתם החזר הערך של הרכב כולל המעמ הינו הערך
                  לחישוב שיש לקחת בחשבון.
                </p>

                <p>
                  במידה ובוצעו תיקונים ברכב או שינויים מותרים מסוימים עליכם
                  להוסיף את הערך של התוספות לערך הקניה.
                </p>

                <div className="bg-green-50 border-r-4 border-green-400 p-4 rounded mt-6">
                  <p className="font-bold text-green-900 mb-2">לסיכום:</p>
                  <ul className="list-disc list-inside space-y-1 text-green-800">
                    <li>יש להזין ערך נטו במטח (לפני מעמ)</li>
                    <li>יש להוסיף את המעמ במידה ולא קיבלתם החזר</li>
                    <li>יש להוסיף ערך תוספות ושינויים.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 2. ערך ההובלה במט"ח */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                2. ערך ההובלה במט"ח
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  עליכם להזין את ערך ההובלה אשר אתם צפויים לשלם או ששילמתם במטח
                  זהה למטח הקניה.
                </p>

                <p>
                  ערך ההובלה בממשק מתייחס גם לערך הובלה פנימית יבשתית מהספק לנמל
                  הטעינה אם שילמתם עליה וגם את ערך ההובלה הימית המשוערת.
                </p>
              </div>
            </section>

            {/* 3. מהו שם המט"ח */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                3. מהו שם המט"ח
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  בשאלה זו עליך לבחור את שם המטח או הקיצור שלו מטבלה של המטבעות
                  ממאגר המכס. עם הבחירת קוד המטבע יקבע שער ההמרה העדכני – שער
                  המכס.
                </p>
              </div>
            </section>

            {/* 4. שער המטח */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                4. שער המטח
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>הוא שער המרה בין מטבע חוץ למטבע שקל מקומי בישראל.</p>

                <p>שער המטח בישראל שונה משער המכס לצרכי חישוב מיסים.</p>

                <p>
                  לפי כללי המכס שער המכס לכל מטבע הוא שער המרה יציג שנקבע ביום
                  שישי מסוים על ידי בנק ישראל והוסיפו לו חצי אחוז (0.5%).
                </p>

                <p>
                  השער שנקבע ביום שישי מסוים נכנס לשימוש במערכות המכס ביום שלישי
                  הקרוב לאחר אותו יום שישי.
                </p>

                <p>
                  באפשרותכם להזין שער המרת מטח שונה שלדעתכם יהיה רלוונטי ליום
                  היבוא.
                </p>
              </div>
            </section>

            {/* 5. ערך הוצאות מקומיות, מיסי נמל */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                5. ערך הוצאות מקומיות, מיסי נמל
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  בשאלה זו עליכם להזין את עלות ההוצאות המקומיות, מיסי הנמל
                  והביטוח הימי בשקלים חדשים.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  מה הם הוצאות מקומיות?
                </h3>

                <p>
                  הוצאות מקומיות הן עלויות שמשלמים לסוכן אוניה או משלח ימי בכדי
                  לקנות בחזרה את החזקה בטובין.
                </p>

                <p>
                  כאשר כלי רכב מגיע על גבי אוניית רכבים הנקראת ro ro במרבית
                  הפעמים אתם או סוכן המכס תידרשו לשלם סכום מסוים לחברת הספנות
                  שהובילה את הרכב כהוצאות מקומיות.
                </p>

                <p>
                  ישנם מספר חברות ספנות ובכדי לקבל אומדן מדויק כדאי לשאול את
                  הספק או את המשלח בחו"ל מה יהיו ההוצאות בישראל.
                </p>

                <p>הוצאות מקומיות נקראות גם עלויות של פקודת מסירה.</p>

                <div className="bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded my-4">
                  <p className="font-semibold text-yellow-900 mb-2">
                    דוגמאות למחירים:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-800">
                    <li>
                      כלי רכב פרטי M1 שמגיע עם חברת ספנות על גבי האוניה (לא
                      במכולה) משלם כ-1000 ש"ח
                    </li>
                    <li>
                      כאשר יש משלח בתמונה ייתכן והסכום יגיע גם ל-2000 שקלים
                      חדשים ואף יותר
                    </li>
                    <li>
                      כאשר אותו כלי הרכב משונע במכולה לבדו או עם רכבים נוספים
                      העלויות המקומיות עולות ויכולות להגיע ל-5000 שח למכולה עם
                      רכב בודד או ל-2500 שח לרכב למכולה עם 2 רכבים
                    </li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  מיסי נמל
                </h3>

                <p>
                  מיסי הנמל הם עלויות שינוע (ניטול) מהאוניה לרציף הנמל ובתוך
                  הרציף הנמל והן משתנות בהתאם למטען.
                </p>

                <p>
                  אם הרכב הגיע לא במכולה אז מיסי הנמל לרכב M1 יהיו בשנת 2025
                  כ-600 שח לרכב במשקל פחות מ-1400 קג,
                </p>

                <p>
                  לרכב במשקל מעל 1400 קג כ-1500 שח ולרכבים כבדים יותר המחיר עולה
                  בהתאם לתעריף החישוב של אותו נמל.
                </p>

                <p>
                  אם הרכב הגיע במכולה לבדו מיסי הנמל יהיו כ-200 שקלים וככל שיש
                  יותר רכבים במכולה הערך 200 מתחלק על פני כמות הרכבים שבמכולה.
                </p>

                <p>אופנועים ונגררים קטנים מגיעים בדרך כלל במכולות.</p>

                <p>כלי רכב מארהב בדרך כלל מגיעים במכולות</p>

                <div className="bg-green-50 border-r-4 border-green-400 p-4 rounded mt-6">
                  <p className="font-bold text-green-900 mb-2">לסיכום:</p>
                  <ul className="list-decimal list-inside space-y-1 text-green-800">
                    <li>
                      הערכת ההוצאות מקומיות לרכבים פרטיים M1 תלוי בשיטת ההובלה
                      בין 1000 שח ל-5000 שח
                    </li>
                    <li>
                      מיסי נמל לרכבים פרטיים M1 תלוי בשיטת ההובלה בין 200 שח
                      ל-1500 שח
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 6. ביטוח */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                6. ביטוח
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>יש להזין גם את עלות פרמיית הביטוח הימי במטבע חוץ או בש"ח</p>
              </div>
            </section>

            {/* 7. האם יש תעודת מקור או הצהרת מקור */}
            <section className="mb-12 bg-gradient-to-r from-purple-50 to-blue-50 p-8 rounded-lg border-2 border-purple-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-purple-500 pb-2">
                7. האם יש תעודת מקור או הצהרת מקור?
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>עליכם לענות על השאלה בכן או לא.</p>

                <p>לישראל יש הסכמי סחר עם מדינות מסוימות.</p>

                <p className="bg-white p-4 rounded border border-purple-300">
                  <strong>מדינות עם הסכמי סחר:</strong> ארה"ב; "האיחוד האירופי"
                  - כל אחד מהארצות החברות באיחוד האירופי המפורטות להלן: אוסטריה,
                  איטליה, אירלנד, אסטוניה, בולגריה, בלגיה, גרמניה, דנמרק, הולנד,
                  הונגריה, יוון, לוכסנבורג, לטביה, ליטה, מלטה, סלובניה, סלובקיה,
                  ספרד, פולין, פורטוגל, פינלנד, צ'כיה, צרפת, קפריסין, קרואטיה,
                  רומניה, שוודיה; "ארצות מרקוסור" - אורוגוואי, ארגנטינה, ברזיל,
                  פרגוואי; "ארצות אפטא" - איסלנד, ליכטנשטיין, נורבגיה, שוויץ;
                  קנדה; קוריאה הדרומית; הודו; טורקיה; בריטניה. יש מדינות נוספות
                  מציע לעיין באתר הממשלתי שמתעדכן מעת לעת בנושא הסכמי סחר.
                </p>

                <p>לכל הסכם יש תנאים שונים למתן פטור ממכס.</p>

                <p className="bg-red-50 border-r-4 border-red-400 p-4 rounded">
                  <strong>חשוב לציין:</strong> יבוא ממדינות שיש עימן הסכם וגם
                  ממדינות שאין איתן הסכם צריך לוודא שכלי הרכב המיובא מיובא בהתאם
                  להנחיות משרד התחבורה השונות ובראשן סוג התקינה.
                </p>

                <p>מותר להביא רכבים עם תקינה אירופאית, קנדית, פדרלית (ארה"ב)</p>

                <p>
                  לדוגמא אם מצאתם רכב במחיר מצחיק אל נא תישבו במחיר אלא תבדקו
                  היטב את מסמכי הרכב ואת נושא התקינה.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  תנאים בסיסיים בהסכמים
                </h3>

                <p>כמעט בכל ההסכמים יש תנאים בסיסיים:</p>

                <ul className="list-disc list-inside mr-6 space-y-2">
                  <li>
                    על כלי הרכב להיות מיוצר באותה מדינה או קבוצת מדינות ואחוז
                    המקוריות של החלקים ממנו מורכב הרכב צריך לעמוד על אחוז מסוים.
                  </li>
                  <li>
                    אם אינך יבואן ישיר שקונה את הרכב ישירות מהיצרן אין סיכוי
                    שתצליח להשיג מסמך שמאשר את אחוז המקוריות. אך למרות זאת שאינך
                    יודע את אחוז המקוריות ישנם פתרונות נוספים שמגנים עליך גם אם
                    אינך יודע זאת.
                  </li>
                  <li>
                    <strong>הובלה ישירה</strong> – על כלי הרכב שאתה רוכש עליו
                    להישלח ישירות מהמדינה שיצרה את הרכב או מקבוצת מדינות שההסכם
                    סחר חל עליהן כמו האיחוד האירופאי
                  </li>
                  <li>
                    לדוגמא רכב שיוצר בגרמניה אם יישלח מאיטליה עדיין המקור שלו
                    יישמר.
                  </li>
                  <li>
                    מסמך שמבטא את המקור בהתאם להסכם הסחר, נקרא תעודת מקור או
                    הצהרת מקור בחשבון הספק.
                  </li>
                  <li>לכל הסכם סחר יש הנחיות מדויקות איזה מסמך נדרש.</li>
                </ul>

                {/* <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  תהליך קוד 815
                </h3>

                <ul className="list-disc list-inside mr-6 space-y-2">
                  <li>בשנת 2020 חלק מיצרני המכוניות באירופה החליטו כהחלטה מסחרית שלא להנפיק יותר הצהרות יצרן שבאמצעותן מונפקת תעודת מקור וכתוצאה מכך לא ניתן לקבל תעודות מקור קבילות שמקנות פטור ממכס.</li>
                  <li>ישנם רכבים שלא משלמים מכס וישנם רכבים שכן – 7%.</li>
                  <li>בחודש ספטמבר לשנת 2023 הוציא מנהל המכס הליך שנקרא קוד 815</li>
                  <li>בתהליך הזה נדרשים כל היבואנים הישירים לדווח למנהל המכס אילו רכבים המיובאים על ידם זכאים לפטור ממכס ולא שילמו מכס בעת הכניסה לישראל.</li>
                  <li>במאגר הממשלתי הנקרא "רשימת רכבים שיובאו בתנאי העדפה על ידי היבואנים הישירים" נמצאות טבלאות אקסל של כל היבואנים הישירים בישראל המדווחים אחת לתקופה ומעדכנים את הטבלאות.</li>
                  <li>
                    אני מצרף כאן קישור להוראת הנוהל של המכס שמסבירה כיצד ניתנת העדפה במכס – כלומר פטור ממכס כאשר הרכב המיובא מתאים לדגמי הרכבים המופיעים בטבלאות היבואנים הישירים.<br />
                    <a 
                      href="https://www.chamber.org.il/media/168074/%D7%94%D7%95%D7%A8%D7%90%D7%AA-%D7%A0%D7%95%D7%94%D7%9C-%D7%9C%D7%A9%D7%97%D7%A8%D7%95%D7%A8-%D7%A8%D7%9B%D7%91%D7%99%D7%9D-%D7%91%D7%A7%D7%95%D7%93-815-002.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      קישור להוראת הנוהל
                    </a>
                  </li>
                  <li>אני ממליץ לכם לקרוא היטב את ההוראה ולחפש רכבים שמתאימים לאלו המופיעים בטבלאות.</li>
                  <li>כאשר יבואן אם מסחרי (שאיננו ישיר) או יבואן ביבוא אישי עומד בתנאים של קוד 815 ומביא רכב מדגם המופיע בטבלאות (ובהתאם להוראה של המכס) הוא יהיה פטור ממכס.</li>
                  <li>המכס לא יבצע תהליך של אימות מקור במקרה של שחרור ממכס באמצעות קוד 815.</li>
                  <li>ייתכן והמכס כן יבדוק את התאמת כלי הרכב לתנאים אך ההגנה הטובה ביותר מפני אימותי מקור שמתבצעים לא מעט הוא תהליך 815.</li>
                </ul>

                <div className="bg-green-50 border-r-4 border-green-400 p-4 rounded mt-6">
                  <p className="font-bold text-green-900 mb-2">לסיכום:</p>
                  <p className="text-green-800">
                    בשאלה זו עליכם לציין בכן או לא האם יש לרכב אותו אתם מבקשים לייבא תעודת מקור, הצהרת מקור או קיום תנאי קוד 815.
                  </p>
                </div> */}
              </div>
            </section>

            {/* 8. האם המשקל הכולל עולה על 3500 ק"ג */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                8. האם המשקל הכולל עולה על 3500 ק"ג?
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  התשובה בלחיצה על הריבוע שלצד השאלה. אם כן אז צריך לסמן וי, אם
                  לא אז לא לסמן כלום.
                </p>

                <p>
                  עליכם לדעת מה הוא המשקל הכולל של כלי הרכב אותו אתם מבקשים
                  לייבא
                </p>

                <p>
                  ניתן לבדוק זאת די בקלות באמצעות צילום של המדבקה שיש על הרכב
                  GVWR לרכב מארה"ב או לרכב מאירופה בסעיף 16.1 בתעודת ה-COC
                </p>

                <p>
                  המשקל הכולל הוא המשקל המקסימלי שהרכב יכול לשקול – הוא מורכב
                  מהמשקל העצמי, משקל הנוסעים, משקל העמסה בתא מטען או בארגז מטען.
                </p>

                <p>
                  ניתן למצוא את המשקל הכולל גם בפרוספקטים של היבואנים הישירים
                  בישראל וכן בתעודות הטכניות של כל רכב COC לרכבים עם תקינה
                  אירופאית או כאמור לבדוק את המדבקה שמודבקת במקומות שונים ברכב
                  המפרטת את המשקל.
                </p>

                <p>בכלי רכב מסוימים יש לזה משמעות רבה בחישוב המס.</p>
              </div>
            </section>

            {/* 9. האם המשקל הכולל עולה על 4500 ק"ג */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                9. האם המשקל הכולל עולה על 4500 ק"ג?
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>התשובה בסימון וי בתיבה</p>

                <p>
                  ביבוא אישי לא ניתן במילא לייבא כלי רכב שהמשקל הכולל שלהם עולה
                  על 3500 ק"ג למעט מקרים חריגים.
                </p>

                <p>ביבוא מסחרי יש לזה משמעות עמוקה שמשפיעה מאד על חישוב המס.</p>

                <p>
                  נקודה נוספת היא העובדה שרכב ששוקל מעל 4500 ק"ג נדרש ברישיון ג'
                  למשאית.
                </p>
              </div>
            </section>

            {/* 10. מה הוא תאריך הייצור של הרכב */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                10. מה הוא תאריך הייצור של הרכב?
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>התשובה במבנה של DD/MM/YYYY</p>

                <p>
                  השאלה הזו נוגעת יותר ליבוא אישי והיא עוזרת לנו לקבוע אם הרכב
                  שלכם עומד בתנאים הבסיסיים ליבוא ובמקרים מסוימים עוזר לנו לקבוע
                  את מס היוקרה שחל על רכבים מסוימים.
                </p>

                <p className="bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded">
                  <strong>חשוב:</strong> ביבוא אישי צריך ליבא רכב ולרשום אותו
                  לתנועה בישראל תוך 24 חודשים ממועד הייצור שלו, אם כך רכב שראיתם
                  שיוצר לפני 22 חודשים ככל הנראה לא תוכלו לייבא אותו בכלל משום
                  שמשרד התחבורה לא יאשר לכם טכנית להגיש את הבקשה לרישיון יבוא
                  עבורו וגם בגלל הזמן הקצר יחסית שנותר מהרגע שרכשתם עד לרגע
                  שעשיתם טסט שספק אם תעמדו בזמן הזה.
                </p>

                <p>
                  משרד התחבורה לא יאפשר הגשת בקשה לרישיון יבוא אם נותרו 45 ימים
                  לתום השנתיים.
                </p>

                <p>
                  בנוסף לא ניתן יהיה לרשום רכב בישראל אם נותרו פחות מ-7 ימים
                  לתום השנתיים.
                </p>

                <p>
                  אני ממליץ לחפש רכבים שיש לכם זמן לרשום את הרכב בהתאם להוראות
                  החוק.
                </p>

                <p>
                  לגבי יבוא מסחרי ידוע שתוך שנה מיום הייצור יש לרשום את הרכב
                  כרכב חדש, צריך לשים לב לעדכונים השונים שמאפשר משרד התחבורה
                  בגין ארכה לרישום.
                </p>

                <p>
                  במידה וחלף הזמן של השנה היבואן מוכרח לדווח בתיק הרכבית שלו
                  שהרכב הינו רכב שאיננו חדש.
                </p>
              </div>
            </section>

            {/* 11. תאריך עלייה לכביש */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                11. תאריך עלייה לכביש
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  ביבוא אישי צריך להזין את תאריך העלייה לכביש של הרכב בחו"ל.
                </p>

                <p>
                  התאריך מופיע ברישיון הרכב אם הרכב מאירופה או ב-CARFAX אם הרכב
                  מארה"ב.
                </p>

                <p>
                  אם לא יודעים תאריך מדויק אז להזין לפחות חודש ושנה ואת היום
                  כ-01
                </p>

                <p className="bg-blue-50 border-r-4 border-blue-400 p-4 rounded">
                  <strong>לדוגמא:</strong> אם תאריך העלייה לכביש הוא חודש יולי
                  2025 אז להזין במערכת 01/07/2025
                </p>

                <p>
                  תאריך העלייה לכביש משמש בכלים שמחשבים מס יוקרה, כך שכל חודש
                  מתאריך העלייה לכביש לתאריך הגשת הצהרת היבוא (המערכת מדמה את
                  הערך שיש לשלם אילו הוגשה הצהרת יבוא ביום החישוב במערכת) מתווסף
                  1.25% פחת שימוש לצרכי קביעת המחיר לצרכן.
                </p>
              </div>
            </section>

            {/* 12. זמן הובלה צפוי */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                12. זמן הובלה צפוי
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>בשאלה זו עליך לבחור את זמן ההובלה הצפוי.</p>

                <p>
                  השאלה נוגעת לכלים שמחשבים מס יוקרה כך שזמן ההובלה הצפוי מוסיף
                  פחת שימוש לצרכי חישוב מחיר לצרכן.
                </p>

                <p>
                  משלוחים מאירופה בדרך כלל לוקחים מספר שבועות ומשלוחים מארה"ב
                  כחודשיים.
                </p>

                <p>כמובן שבהובלה ימית תמיד יש שינויים בלתי צפויים.</p>
              </div>
            </section>

            {/* 13. בחירת המחיר לצרכן */}
            <section className="mb-12 bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-lg border-2 border-green-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-green-500 pb-2">
                13. בחירת המחיר לצרכן
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-3">
                  ביבוא אישי:
                </h3>

                <p>
                  בשאלה זו צריך להזין את המחיר לצרכן של הדגם הספציפי של הרכב כפי
                  שקובע אותו היבואן הישיר לישראל. מערכת המכס בדרך כלל לא מזהה את
                  המחיר לצרכן של היבואן הישיר ולכן היא מבצעת רצף נוסחאות שמבצעות
                  הנדסה לאחור של המחיר לצרכן של הרכב הספציפי וקובעת את המחיר
                  לצרכן של הרכב אילו הוא היה חדש, לאחר מכן היא מחשבת את מס
                  היוקרה של הרכב אם מחירו לצרכן עולה על 300,000
                </p>

                <p>
                  מערכת CARTAX מבצעת את חישוב המכס במדויק ומציגה לכם את התוצאה.
                </p>

                <p>
                  באפשרותכם לבחור בתוצאה שמערכת CARTAX מציגה או לבחור ערך אחר.
                </p>

                <p className="bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded">
                  <strong>חשוב:</strong> בכדי להתקדם לדף התוצאות חייבים להזין
                  מחיר לצרכן גם אם מחירו פחות מ-300,000 ₪.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  ביבוא מסחרי:
                </h3>

                <p>
                  מערכת CARTAX מבקשת שתזינו את המחיר לצרכן שהוא המחיר שאתם
                  עשויים למכור את הרכב כולל מע"מ. בכדי לתת לכם כיוון מהו המחיר
                  לצרכן המערכת מציגה לכם את עלות הרכב כולל המיסים עד כה ללא
                  מרכיב מס יוקרה אם יש.
                </p>

                <p>
                  לאחר הזנת המחיר לצרכן תעברו לדף התוצאות ותראו את פירוט
                  התוצאות.
                </p>

                <p>
                  באפשרותכם לחזור אחורנית ולשנות את המחיר לצרכן כך שיתאים לאחוז
                  הרווח אליו אתם מכוונים.
                </p>
              </div>
            </section>

            {/* 14. בחירת קבוצת רמת זיהום אוויר */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                14. בחירת קבוצת רמת זיהום אוויר
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>המערכת מבקשת שתזינו את קבוצת זיהום האוויר 1 עד 15ג</p>

                <p>1 היא הרמת זיהום הכי נמוכה</p>

                <p>15ג' היא רמת הזיהום הכי גבוהה.</p>

                <p>בכדי לדעת מה היא רמת הזיהום יש כמה אפשרויות.</p>

                <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-3">
                  אפשרויות לבדיקה:
                </h3>

                <ul className="list-disc list-inside mr-6 space-y-2">
                  <li>
                    <strong>אפשרות ראשונה:</strong> למצוא מידע בפירוט טכני של
                    הדגם של היבואן הישיר.
                  </li>
                  <li>
                    <strong>אפשרות שניה:</strong> להשתמש במחשבון חישוב רמת זיהום
                    האויר של CARTAX שמבצע חישוב לפי נוסחת החישוב של נוהל
                    018-2014 של משרד התחבורה.
                  </li>
                </ul>

                <p>
                  במחשבון יש לבחור סוג התקינה אירופאית או פדרלית ולהזין נתונים
                  בהתאם.
                </p>

                <p>
                  אם אינכם יודעים את הנתונים ניתן להשיגם באמצעות תעודת ה-COC
                  לתקינה אירופאית או באמצעות אתר EPA שם מדווחים דגמי רכבים לפי
                  שנתונים בקבצי אקסל.
                </p>

                <p>
                  <a
                    href="https://www.epa.gov/compliance-and-fuel-economy-data/data-cars-used-testing-fuel-economy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    קישור לאתר EPA
                  </a>
                </p>

                <p>
                  בקבצי האקסל יש לאתר את דגם הרכב שלכם ולאתר את נתוני דיווח רמות
                  הזיהום הנדרשות לפי Highway – HWFE ולפי City – FTP כפי שמתבקש
                  במחשבון CARTAX.
                </p>

                <p className="bg-red-50 border-r-4 border-red-400 p-4 rounded">
                  <strong>**הערה –</strong> לגבי נתון CO2 בתקינה אירופאית יש
                  להזין נתון CO2 COMBINED כפי שמופיע בתעודת ה-COC בסעיף 49.1
                  בסוף הטבלה.
                </p>
              </div>
            </section>

            {/* 15. בחירת מערכות אבזור בטיחותי */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                15. בחירת מערכות אבזור בטיחותי
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  ביבוא אישי:
                </h3>

                <p>
                  לאחר קבלת התוצאה ניתן סמן את מערכות הבטיחות שיש ברכב וזאת כדי
                  להעריך כמה החזר מס תהיו זכאים לאחר שהרכב יירשם לתנועה.
                </p>

                <p>בנוגע לרכבים חדשים או משומשים עד 24 חודשים.</p>

                <p>
                  לאחר הטסט של הרכב ניתן לקבל החזר מיסים, יש לפנות לעמיל המכס
                  שטיפל בשחרור הרכב מהמכס לצורך הגשת תביעה להחזר מיסים.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  ביבוא מסחרי:
                </h3>

                <p>
                  הנחת האבזור הבטיחותי ניתנת ליבואן בשלב הגשת הצהרת היבוא כחלק
                  מנתוני תיק הרכבית שהפיק היבואן המסחרי.
                </p>
              </div>
            </section>

            {/* יצירת קשר */}
            <section className="mt-12 bg-blue-50 border-2 border-blue-300 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <span>💬</span>
                <span>צריכים עזרה נוספת?</span>
              </h2>

              <div className="space-y-3 text-gray-800">
                <p className="text-lg">
                  אם דף הסברי חישוב לא ענה על שאלתכם, אל תהססו צרו קשר לקבלת
                  מידע מקצועי ממערכת CARTAX.
                </p>

                <div className="mt-4">
                  <p>
                    <strong>דוא"ל:</strong>{" "}
                    <a
                      href="mailto:info@cartax.org"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      info@cartax.org
                    </a>
                  </p>
                  <p>
                    <strong>טלפון:</strong>{" "}
                    <a
                      href="tel:054-4512136"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      054-4512136
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Print Button */}
          <div className="mt-8 flex justify-center print:hidden">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              aria-label="הדפס עמוד"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              <span>🖨️ הדפס עמוד</span>
            </button>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default CalculationExplanationPage;
